import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import WineCard from '../components/WineCard';
import fotoVinha1 from '../assets/foto_vinha1.jpg';
import fotoVinha2 from '../assets/foto_vinha2.jpg';
import fotoDouroSuperior from '../assets/foto_vinha_rotacao1.jpg';
import fotoVilarica       from '../assets/DSC01770.jpg';
import fotoOrigem         from '../assets/douronostrum00000044.jpg';
import fotoTradicaoExcelencia from '../assets/vinha_tradicao_excelencia.jpg';
import { useT, useLanguage } from '../i18n/LanguageContext';
import { useWines } from '../context/WinesContext';

/* ── Frames da garrafa a rodar ───────────────────────────── */
import g1  from '../assets/garrafas/garrafa_1.png';
import g2  from '../assets/garrafas/garrafa_2.png';
import g3  from '../assets/garrafas/garrafa_3.png';
import g4  from '../assets/garrafas/garrafa_4.png';
import g5  from '../assets/garrafas/garrafa_5.png';
import g6  from '../assets/garrafas/garrafa_6.png';
import g7  from '../assets/garrafas/garrafa_7.png';
import g8  from '../assets/garrafas/garrafa_8.png';
import g9  from '../assets/garrafas/garrafa_9.png';
import g10 from '../assets/garrafas/garrafa_10.png';
import g11 from '../assets/garrafas/garrafa_11.png';
import g12 from '../assets/garrafas/garrafa_12.png';
import g13 from '../assets/garrafas/garrafa_13.png';
import g14 from '../assets/garrafas/garrafa_14.png';
import g15 from '../assets/garrafas/garrafa_15.png';
import g16 from '../assets/garrafas/garrafa_16.png';
import g17 from '../assets/garrafas/garrafa_17.png';
import g18 from '../assets/garrafas/garrafa_18.png';
import g19 from '../assets/garrafas/garrafa_19.png';
import g20 from '../assets/garrafas/garrafa_20.png';
import g21 from '../assets/garrafas/garrafa_21.png';
import g22 from '../assets/garrafas/garrafa_22.png';
import g23 from '../assets/garrafas/garrafa_23.png';
import g24 from '../assets/garrafas/garrafa_24.png';
import g25 from '../assets/garrafas/garrafa_25.png';
import g26 from '../assets/garrafas/garrafa_26.png';

const BOTTLE_FRAMES = [
  g1, g2, g3, g4, g5, g6, g7, g8, g9, g10,
  g11, g12, g13, g14, g15, g16, g17, g18, g19, g20,
  g21, g22, g23, g24, g25, g26,
];

const FRAMES_PER_ROTATION = BOTTLE_FRAMES.length; // 26
const ROTATIONS = 3;
const TOTAL_STEPS = FRAMES_PER_ROTATION * ROTATIONS; // 78

/* Frases que aparecem em sequência durante o scroll do hero.
   Cada uma "mantém-se" durante ~1/N do scroll, com fade in/out
   nos extremos para a transição ser suave (sem dois textos visíveis
   ao mesmo tempo). */
const HERO_PHRASE_KEYS = [
  'home.hero.title.familia',  // O sonho de um Pai (1ª)
  'home.hero.title.quinta',   // A vontade de dois filhos
  'home.hero.title.vinho',    // Cada gota carrega o Douro…
];
const PHRASE_FADE = 0.18; // % de cada segmento usado para fade in/out

export default function Home() {
  const t = useT();
  const { lang } = useLanguage();
  const { wines } = useWines();
  const featuredWines = wines.filter(w => w.badge === 'Destaque').slice(0, 3);
  const wrapperRef      = useRef(null);
  const imgRef          = useRef(null);
  const titleRef        = useRef(null);
  const lastFrameRef    = useRef(-1);
  const lastPhraseRef   = useRef(0);

  /* — Preload de todos os frames para evitar flicker — */
  useEffect(() => {
    BOTTLE_FRAMES.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  /* — Atualiza o frame da garrafa + a frase do hero em função do scroll —
       Garrafa: single frame swap (sem ghosting).
       Frase:   uma de cada vez, com fade in/out nos extremos do segmento
                — durante a transição a opacidade vai a 0 antes de trocar
                o texto, evitando ver duas frases sobrepostas. */
  useEffect(() => {
    let rafId = 0;

    const update = () => {
      const wrapper = wrapperRef.current;
      const img     = imgRef.current;
      if (!wrapper || !img) return;

      const rect = wrapper.getBoundingClientRect();
      const viewportH  = window.innerHeight;
      const scrollable = rect.height - viewportH;
      const scrolled   = -rect.top;

      const title = titleRef.current;

      // Mobile (≤1024px) — hero não é sticky, mas a garrafa roda
      // 1 vez enquanto a hero scrolla para fora do viewport.
      // A frase fica sempre na primeira (não há scroll suficiente
      // para fade entre várias).
      const isMobile = window.innerWidth <= 1024;
      if (isMobile) {
        // Progresso linear: 0 quando o topo da hero está no topo do
        // viewport; 1 quando a hero já passou completamente para cima.
        const heroProgress = Math.max(0, Math.min(1, (-rect.top) / rect.height));
        const stepIndex    = Math.round(heroProgress * (FRAMES_PER_ROTATION - 1));
        if (lastFrameRef.current !== stepIndex) {
          img.src = BOTTLE_FRAMES[stepIndex];
          lastFrameRef.current = stepIndex;
        }
        if (title) {
          if (lastPhraseRef.current !== 0) {
            title.textContent = t(HERO_PHRASE_KEYS[0]);
            lastPhraseRef.current = 0;
          }
          title.style.opacity   = '1';
          title.style.transform = 'translateY(0)';
        }
        return;
      }

      // Wrapper sem altura extra (caso de fallback) → estado inicial.
      if (scrollable <= 0) {
        if (lastFrameRef.current !== 0) {
          img.src = BOTTLE_FRAMES[0];
          lastFrameRef.current = 0;
        }
        if (title) {
          if (lastPhraseRef.current !== 0) {
            title.textContent = t(HERO_PHRASE_KEYS[0]);
            lastPhraseRef.current = 0;
          }
          title.style.opacity   = '1';
          title.style.transform = 'translateY(0)';
        }
        return;
      }

      const progress = Math.max(0, Math.min(1, scrolled / scrollable));

      /* — Garrafa — */
      const stepIndex = Math.round(progress * TOTAL_STEPS);
      const frame     = stepIndex % FRAMES_PER_ROTATION;
      if (lastFrameRef.current !== frame) {
        img.src = BOTTLE_FRAMES[frame];
        lastFrameRef.current = frame;
      }

      /* — Frase do hero (rise + fade) —
         Cada frase: sobe e desaparece com fade, próxima entra de
         baixo com fade in. A primeira frase está visível desde o
         início (segmentProg < 0.5: fica fixa em translateY 0, op 1). */
      if (title) {
        const segments     = HERO_PHRASE_KEYS.length;
        const segmentSize  = 1 / segments;
        const segmentIndex = Math.min(
          Math.floor(progress / segmentSize),
          segments - 1
        );
        const segmentProg  = (progress - segmentIndex * segmentSize) / segmentSize;

        const RISE = 60; // px que cada frase percorre durante o seu segmento

        let translateY = (RISE / 2) - RISE * segmentProg;
        let opacity;

        if (segmentIndex === 0) {
          // Primeira frase — começa visível, só faz fade out + rise out
          translateY = Math.min(0, translateY);
          opacity = segmentProg < 0.5 ? 1 : (1 - segmentProg) / 0.5;
        } else {
          // Outras frases — fade in (entra de baixo), fade out (sai por cima)
          opacity = segmentProg < 0.5
            ? segmentProg / 0.5
            : (1 - segmentProg) / 0.5;
        }

        if (lastPhraseRef.current !== segmentIndex) {
          title.textContent = t(HERO_PHRASE_KEYS[segmentIndex]);
          lastPhraseRef.current = segmentIndex;
        }
        title.style.transform = `translateY(${translateY}px)`;
        title.style.opacity   = String(opacity);
      }
    };

    const onScroll = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(update);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      cancelAnimationFrame(rafId);
    };
  }, [t]);


  return (
    <>
      {/* ════════════════════════════════════════
          HERO + scroll wrapper
          O wrapper tem altura (ROTATIONS + 1) * 100vh para que,
          enquanto a hero está fixa no viewport (sticky), o scroll
          da página faça a garrafa rodar 3 vezes.
      ════════════════════════════════════════ */}
      <div ref={wrapperRef} className="hero-scroll-wrapper">
        <section
          className="hero hero--sticky hero--bw"
          style={{
            '--hero-bg': `url(${fotoVinha1})`,
            background: 'linear-gradient(160deg, rgba(14,7,4,0.3) 0%, rgba(14,7,4,0.13) 100%)',
          }}
        >
          <div className="hero__overlay" />

          <div className="hero__content container home-hero__grid">
            {/* Coluna esquerda — texto */}
            <div className="home-hero__col-text">
              <div className="home-hero__phrase-track">
                <h1
                  ref={titleRef}
                  className="home-hero__title"
                >
                  {t(HERO_PHRASE_KEYS[0])}
                </h1>
              </div>
              <p className="t-label anim-fade-up" style={{ color: '#ffffff', marginTop: 'var(--sp-5)' }}>
                <span className="overline" />
                {t('home.hero.eyebrow')}
              </p>
            </div>

            {/* Coluna central — garrafa rotativa */}
            <img
              ref={imgRef}
              src={BOTTLE_FRAMES[0]}
              alt={t('home.hero.photoPlaceholder')}
              className="home-hero__photo anim-fade-up delay-2"
              draggable="false"
            />

            {/* Coluna direita — botões verticais */}
            <div className="home-hero__col-buttons anim-fade-up delay-3">
              <Link to="/loja" className="btn btn-lg btn--on-dark">
                {t('home.hero.cta1')}
              </Link>
            </div>
          </div>
        </section>
      </div>


      {/* ════════════════════════════════════════
          3 SEPARADORES — Douro Superior / Vale Vilariça / Origem
          Coluna vertical, cada card full-width com foto de fundo
          e "etiqueta" branca de texto encostada a um canto (alterna
          esquerda/direita para criar ritmo visual).
      ════════════════════════════════════════ */}
      <section className="section">
        <div className="container">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-6)' }}>
            {[
              { to: '/douro-superior', keyBase: 'territorio.douroSup', bg: fotoDouroSuperior, zoom: true },
              { to: '/vale-vilarica',  keyBase: 'territorio.vilarica', bg: fotoVilarica      },
              { to: '/origem',         keyBase: 'territorio.origem',   bg: fotoOrigem        },
            ].map(({ to, keyBase, bg, zoom }, i) => (
              <Link
                key={to}
                to={to}
                className={`territorio-card${i % 2 === 1 ? ' territorio-card--right' : ''}${zoom ? ' territorio-card--zoom' : ''}`}
                style={{ backgroundImage: `url("${bg}")` }}
              >
                <div className="territorio-card__label">
                  <p className="t-label" style={{ color: 'var(--clr-vinho)', marginBottom: 'var(--sp-3)' }}>
                    {t(`${keyBase}.eyebrow`)}
                  </p>
                  <h3 className="t-h3" style={{ marginBottom: 'var(--sp-5)' }}>
                    {t(`${keyBase}.title`)}
                  </h3>
                  <p className="t-body t-light" style={{ marginBottom: 'var(--sp-6)', whiteSpace: 'pre-line' }}>
                    {t(`${keyBase}.short`).split('\n\n')[0]}
                  </p>
                  <span className="t-label" style={{ color: 'var(--clr-vinho)' }}>
                    {t('territorio.readMore')} →
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>


      {/* ════════════════════════════════════════
          VINHOS EM DESTAQUE
      ════════════════════════════════════════ */}
      <section className="section">
        <div className="container">
          <div className="section-header--centered">
            <p className="t-label" style={{ color: '#000000', marginBottom: 'var(--sp-4)' }}>
              {t('home.featured.eyebrow')}
            </p>
            <h2 className="t-h2" style={{ textTransform: 'uppercase' }}>{t('home.featured.title')}</h2>
            <p className="t-body-lg t-light" style={{ marginTop: 'var(--sp-4)' }}>
              {t('home.featured.lead')}
            </p>
          </div>

          <div className="grid-3">
            {featuredWines.map(w => (
              <WineCard key={w.id} {...w} description={lang === 'en' ? w.description.en : w.description.pt} />
            ))}
          </div>

          <div className="t-center" style={{ marginTop: 'var(--sp-12)' }}>
            <Link to="/loja" className="btn btn-lg">
              {t('home.featured.cta')}
            </Link>
          </div>
        </div>
      </section>


      {/* ════════════════════════════════════════
          ABOUT STRIP
      ════════════════════════════════════════ */}
      <section
        className="section"
        style={{
          background: `linear-gradient(160deg, rgba(14,7,4,0.65) 0%, rgba(14,7,4,0.35) 100%), url(${fotoTradicaoExcelencia}) center/cover no-repeat`,
          color: '#ffffff',
          minHeight: '80vh',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <div className="container" style={{ width: '100%' }}>
          <div style={{ maxWidth: '640px' }}>
            <p className="t-label" style={{ color: '#ffffff', marginBottom: 'var(--sp-4)' }}>
              <span className="overline" />
              {t('home.about.eyebrow')}
            </p>
            <h2 className="t-h2" style={{ color: '#ffffff', marginBottom: 'var(--sp-6)' }}>
              {t('home.about.title')}
            </h2>
            <p className="t-body-lg" style={{ color: 'rgba(255,255,255,0.7)', marginBottom: 'var(--sp-8)' }}>
              {t('home.about.lead')}
            </p>
            <Link to="/sobre" className="btn btn--on-dark">
              {t('home.about.cta')}
            </Link>
          </div>
        </div>
      </section>


      {/* ════════════════════════════════════════
          CTA FINAL
      ════════════════════════════════════════ */}
      <section className="section bg-areia t-center">
        <div className="container--narrow">
          <p className="t-label" style={{ color: '#000000', marginBottom: 'var(--sp-4)' }}>
            {t('home.cta.eyebrow')}
          </p>
          <h2 className="t-h2" style={{ marginBottom: 'var(--sp-5)' }}>
            {t('home.cta.title')}
          </h2>
          <p className="t-body-lg t-light" style={{ marginBottom: 'var(--sp-8)' }}>
            {t('home.cta.lead')}
          </p>
          <Link to="/contacto" className="btn btn-lg">
            {t('home.cta.button')}
          </Link>
        </div>
      </section>
    </>
  );
}
